import React, { useCallback, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperRef } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import CarouselNavArrow from './CarouselNavArrow';
import type { Swiper as SwiperClass } from 'swiper/types';
import styled from 'styled-components';

const StyledSwiper = styled(Swiper)`
  .swiper-container,
  .swiper-wrapper,
  .swiper-slide {
    overflow: visible;
  }
`;

const Carousel = (props: React.PropsWithChildren<SwiperProps>) => {
  const sliderRef = useRef<SwiperRef>(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) {
      return;
    }
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) {
      return;
    }
    sliderRef.current.swiper.slideNext();
  }, []);

  const handleSlideChange = (swiper: SwiperClass) => {
    setShowPrev(!swiper.isBeginning);
    setShowNext(!swiper.isEnd);

    if (props?.onSlideChange) {
      props.onSlideChange(swiper);
    }
  };

  const handleInitialize = (swiper: SwiperClass) => {
    setShowPrev(!swiper.isBeginning);
    setShowNext(!swiper.isEnd);

    if (props?.onInit) {
      props.onInit(swiper);
    }
  };

  return (
    <StyledSwiper
      ref={sliderRef}
      {...props}
      modules={[Navigation, ...(props?.modules ?? [])]}
      onInit={handleInitialize}
      onSlideChange={handleSlideChange}
      /**
       * Issue: https://stackoverflow.com/questions/50690843/mouseup-events-are-not-working-inside-swiper
       * Swiper will prevent all default mouse up and mouse down events in children..
       */
      touchStartPreventDefault={false}
    >
      {props.children}
      {showPrev && <CarouselNavArrow type="prev" onClick={handlePrev} />}
      {showNext && <CarouselNavArrow type="next" onClick={handleNext} />}
    </StyledSwiper>
  );
};

export default Carousel;
